"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import AuthSocialButtons from "@/components/landing/auth/auth-social-buttons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ANALYTICS_EVENTS, getEmailDomain, trackEvent } from "@/lib/analytics";
import { API_BASE_URL, postJson } from "@/lib/api";
import { getSafeInternalRedirect } from "@/lib/auth-redirect";
import { buildOauthReturnSearchParams } from "@/lib/oauth-return";
import { showToast } from "@/lib/toast";
import { getErrorMessage } from "@/lib/utils";

type RegisterRequestResponse = {
  success: boolean;
  requiresVerification: boolean;
  email: string;
};
type LoginResponse = { accessToken: string };

const SignupPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationStep, setVerificationStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signinHref, setSigninHref] = useState("/signin");
  const passwordIsValid = password.length >= 6;
  const showPasswordError = password.length > 0 && !passwordIsValid;

  const getRedirectTo = () => {
    const from =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("from")
        : null;
    return getSafeInternalRedirect(from);
  };

  useEffect(() => {
    const from =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("from")
        : null;
    const safeFrom = getSafeInternalRedirect(from, "");
    setSigninHref(
      safeFrom ? `/signin?from=${encodeURIComponent(safeFrom)}` : "/signin",
    );
  }, []);

  const oauth = (provider: "google" | "github") => {
    let query = "";
    try {
      query = `?${buildOauthReturnSearchParams({
        returnTo: getRedirectTo(),
      }).toString()}`;
    } catch {}
    trackEvent(ANALYTICS_EVENTS.authOauthStarted, {
      source: "signup_page",
      provider,
    });
    window.location.href = `${API_BASE_URL}/auth/${provider}${query}`;
  };

  const handleSignup = async () => {
    if (!email.trim() || !passwordIsValid) return;
    setLoading(true);
    try {
      const data = await postJson<RegisterRequestResponse>("/auth/register", {
        email: email.trim(),
        password,
        name: name.trim() || undefined,
      });
      const normalizedEmail = data?.email ?? email.trim();
      if (normalizedEmail) setEmail(normalizedEmail);
      trackEvent(ANALYTICS_EVENTS.authSignupCodeRequested, {
        source: "signup_page",
        request_type: "initial",
        email_domain: getEmailDomain(normalizedEmail) ?? "unknown",
      });
      setVerificationStep(true);
      setVerificationCode("");
      showToast.success("We sent you a verification code. Check your inbox.");
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Failed to send verification code",
      );
      trackEvent(ANALYTICS_EVENTS.authFlowFailed, {
        source: "signup_page",
        stage: "request_code",
        message,
      });
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.trim().length !== 6) return;
    setLoading(true);
    try {
      await postJson<LoginResponse>("/auth/register/verify", {
        email: email.trim(),
        code: verificationCode.trim(),
      });
      trackEvent(ANALYTICS_EVENTS.authSignupCompleted, {
        source: "signup_page",
        method: "email_code",
        email_domain: getEmailDomain(email) ?? "unknown",
      });
      showToast.success("Account created");
      window.location.assign(getRedirectTo());
    } catch (error) {
      const message = getErrorMessage(error, "Failed to verify code");
      trackEvent(ANALYTICS_EVENTS.authFlowFailed, {
        source: "signup_page",
        stage: "verify_code",
        message,
      });
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await postJson<RegisterRequestResponse>("/auth/register", {
        email: email.trim(),
        password,
        name: name.trim() || undefined,
      });
      trackEvent(ANALYTICS_EVENTS.authSignupCodeRequested, {
        source: "signup_page",
        request_type: "resend",
        email_domain: getEmailDomain(email) ?? "unknown",
      });
      showToast.success("Code resent. Check your inbox.");
    } catch (error) {
      showToast.error(getErrorMessage(error, "Failed to resend code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen lg:min-h-[calc(100vh-200px)] items-center justify-center py-[100px] lg:pb-16 lg:pt-[128px]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">
            Create your account
          </h1>
          <p className="text-slate-500 mt-2">
            Create your account, connect Linear, and choose what customers can
            see.
          </p>
          <p className="mt-3 text-sm font-medium text-indigo-600">
            7-day free trial · No credit card required
          </p>
          <ol className="mt-4 grid grid-cols-3 gap-2 text-xs text-slate-500">
            {["Create account", "Connect Linear", "Publish work"].map(
              (label, index) => (
                <li key={label} className="flex flex-col items-center gap-1">
                  <span className="flex size-5 items-center justify-center rounded-full bg-indigo-50 font-semibold text-indigo-600">
                    {index + 1}
                  </span>
                  <span>{label}</span>
                </li>
              ),
            )}
          </ol>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <AuthSocialButtons
            onGoogle={() => oauth("google")}
            onGithub={() => oauth("github")}
            disabled={loading}
          />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs text-slate-500">
              <span className="bg-white px-3">or continue with email</span>
            </div>
          </div>

          {verificationStep ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
                We emailed a 6-digit code to{" "}
                <span className="font-semibold text-slate-900">{email}</span>.
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">
                  Verification code
                </Label>
                <Input
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="h-11"
                />
              </div>
              <Button
                className="w-full h-11"
                disabled={loading || verificationCode.trim().length !== 6}
                onClick={handleVerify}
              >
                {loading ? "Verifying..." : "Verify & create account"}
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  onClick={handleResend}
                >
                  Resend code
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={loading}
                  onClick={() => {
                    setVerificationStep(false);
                    setVerificationCode("");
                  }}
                >
                  Change email
                </Button>
              </div>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleSignup();
              }}
            >
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">
                  Name (optional)
                </Label>
                <Input
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">Email</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">Password</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  minLength={6}
                  aria-invalid={showPasswordError}
                  aria-describedby="signup-password-help"
                  className={`h-11 rounded-full ${
                    showPasswordError ? "border-red-500" : ""
                  }`}
                  required
                />
                <p
                  id="signup-password-help"
                  className={`text-xs ${
                    showPasswordError ? "text-red-600" : "text-slate-500"
                  }`}
                >
                  Use at least 6 characters.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading || !email.trim() || !passwordIsValid}
              >
                {loading ? "Sending code..." : "Create account"}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{" "}
          <Link
            href={signinHref}
            className="text-indigo-600 font-medium hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
};

export default SignupPage;
